import { useCallback, useEffect, useState } from 'react'
import posthog from 'posthog-js'
import type { ThematicView } from './api'

export const COLOUR_BY_PARAM = 'colour_by'
export const NONE = 'none'

export type PickSource = 'suggestion' | 'list' | 'search'

const storageKey = (view: ThematicView) => `thematic:${view}`

function readInitial(view: ThematicView, defaultKey: string): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get(COLOUR_BY_PARAM)
  const raw = fromUrl ?? safeGet(storageKey(view)) ?? defaultKey
  return raw === NONE ? null : raw
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Private mode: the URL still carries the choice.
  }
}

/**
 * The "Colour by" choice of one view. Kept in the URL (`colour_by`) so a link
 * shares it, and in local storage per view so it survives a reload. `null` is
 * "Aucune".
 */
export function useThematicChoice(view: ThematicView, defaultKey: string) {
  const [key, setKeyState] = useState<string | null>(() => readInitial(view, defaultKey))
  const [isolated, setIsolated] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    params.set(COLOUR_BY_PARAM, key ?? NONE)
    const qs = params.toString()
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${qs}${window.location.hash}`)
    safeSet(storageKey(view), key ?? NONE)
  }, [key, view])

  const setKey = useCallback(
    (next: string | null, source?: PickSource, coverage?: { covered?: number, total?: number }) => {
      setKeyState(next)
      setIsolated(null)
      if (next && source) {
        try {
          posthog.capture('thematic_selected', {
            view,
            key: next,
            source,
            coverage_covered: coverage?.covered,
            coverage_total: coverage?.total,
          })
        } catch {
          // Analytics must never break the plan.
        }
      }
    },
    [view],
  )

  /** Clicking a class isolates it, clicking it again lifts the isolation. */
  const toggleIsolated = useCallback((value: string) => {
    setIsolated((current) => (current === value ? null : value))
  }, [])

  return { key, setKey, isolated, setIsolated, toggleIsolated }
}
